import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, query, onSnapshot, serverTimestamp, doc, getDocs, getDoc, updateDoc, where, orderBy, limit } from '../firebase.config';
import { enforcedAddDoc as addDoc, enforcedSetDoc as setDoc, enforcedDeleteDoc as deleteDoc } from '../utils/enforcedTracking';
import { useLanguage } from '../contexts/LanguageContext';
import { useTableSort } from '../utils/tableSort';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { getCollectionForCompany, normalizeCompanyDocSnapshot } from '../utils/dataAdapter';
import GSTInput from './GSTInput';
import ContactSelector from './ContactSelector';
import ContactSuggestion from './ContactSuggestion';
import contactSyncService from '../services/ContactSyncService';
import { searchContactByPhone, normalizePhone } from '../utils/contactMatcher';
import { useNotification } from '../contexts/NotificationContext';
import { useErrorHandler } from '../hooks/useErrorHandler';
// 🔒 PHASE 3: Import secure cache manager for cache invalidation
import { secureCacheManager } from '../utils/secureCacheManager';
import LoadingScreen from './LoadingScreen';
import { useComponentLoading } from '../contexts/LoadingContext';
import {
  normalizeTagName,
  normalizeVendorServiceTags,
  isPartyServiceVendor,
  dedupeServiceTagsForDisplay,
} from '../utils/serviceTagRegistry';
import { ConfirmationModal, MODAL_SECTIONS } from './Modal';
import PartiesCatalogTab from './Parties/PartiesCatalogTab';
import PartiesTabNav, { PartiesTabNavMobile } from './Parties/PartiesTabNav';
import { Users, TrendingUp, Building2 } from 'lucide-react';
import { isSupplierPartyType, partyMatchesTypeFilter, PARTIES_THEME } from './Parties/partiesTheme';
import {
    calculatePartyFyTotals,
    createOpeningBalancePaymentEntry,
} from '../utils/partyOpeningBalanceUtils';

const Parties = ({ db, userId, isAuthReady, appId }) => {
    const { currentCompany } = useEnhancedCompany();
    const companyId = currentCompany?.companyId || currentCompany?.id;
    const { t } = useLanguage();
    const { success, error, warning, info } = useNotification();
    const { handleError, handleValidationError, showWarning } = useErrorHandler();
    
    // Mobile/Android detection - STRICT: Only for Capacitor (Android/iOS app)
    const [isMobileDevice, setIsMobileDevice] = useState(false);
    
    useEffect(() => {
        const checkMobileDevice = () => {
            // ONLY show for Capacitor (actual Android/iOS app), NOT for web browsers
            const isCapacitor = typeof window !== 'undefined' && window.Capacitor;
            setIsMobileDevice(isCapacitor);
        };
        checkMobileDevice();
    }, []);
    const [firmName, setFirmName] = useState('');
    const [personName, setPersonName] = useState('');
    const [address, setAddress] = useState('');
    const [contact, setContact] = useState('');
    const [email, setEmail] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    const [gstin, setGstin] = useState('');
    const [gstType, setGstType] = useState('Unregistered'); // New field for GST type
    const location = useLocation();
    const [partyType, setPartyType] = useState(() => {
        const p = new URLSearchParams(location.search).get('type');
        return p === 'Supplier' ? 'Supplier' : 'Buyer';
    });
    const [message, setMessage] = useState('');
    const [partiesList, setPartiesList] = useState([]);
    const [editingPartyId, setEditingPartyId] = useState(null);
    const [city, setCity] = useState('');
    const [stateName, setStateName] = useState('');
    const [pincode, setPincode] = useState('');
    const [pan, setPan] = useState('');
    const [creditPeriod, setCreditPeriod] = useState('0');
    const [creditLimit, setCreditLimit] = useState('0');
    const [isServiceVendor, setIsServiceVendor] = useState(false);
    const [vendorServiceTags, setVendorServiceTags] = useState([]);
    const [serviceTagMaster, setServiceTagMaster] = useState([]);
    const [serviceTagOptions, setServiceTagOptions] = useState([]);

    // Company details state (for GST state code matching)
    const [companyDetails, setCompanyDetails] = useState(null);

    // GST Validation State
    const [gstValidation, setGstValidation] = useState({
        isValid: null,
        message: '',
        stateInfo: null,
        panInfo: null
    });
    const [openingBalance, setOpeningBalance] = useState('0');
    const [sameAsWhatsapp, setSameAsWhatsapp] = useState(false);
    
    // New state for tabs and opening balance management
    const [activeTab, setActiveTab] = useState('information');
    const [selectedParty, setSelectedParty] = useState(null);
    const [useManualBalance, setUseManualBalance] = useState(true);
    const [manualBalance, setManualBalance] = useState('0');
    const [manualBalanceDate, setManualBalanceDate] = useState('');
    const [manualBalanceRemarks, setManualBalanceRemarks] = useState('');
    
    // New state for financial year-end calculations
    const [financialYear, setFinancialYear] = useState('');
    const [calculationData, setCalculationData] = useState([]);
    const [isCalculating, setIsCalculating] = useState(false);
    const [calculationDate, setCalculationDate] = useState(new Date().toISOString().split('T')[0]);
    const [auditTrail, setAuditTrail] = useState([]);
    
    // Contact auto-fill state
    const [contactSuggestion, setContactSuggestion] = useState(null);
    const [isSearchingContact, setIsSearchingContact] = useState(false);
    const [phoneDebounceTimer, setPhoneDebounceTimer] = useState(null);
    const [showCalculationPreview, setShowCalculationPreview] = useState(false);
    const [showAuditTrail, setShowAuditTrail] = useState(false);
    
    // Transaction tracking state variables
    const [partyTransactions, setPartyTransactions] = useState([]);
    const [partyAgingData, setPartyAgingData] = useState([]);
    const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
    const [isLoadingAging, setIsLoadingAging] = useState(false);
    
    // Cached outstanding calculation for selected party
    const [calculatedOutstanding, setCalculatedOutstanding] = useState(null);
    const [isCalculatingOutstanding, setIsCalculatingOutstanding] = useState(false);

    // Party selection dropdown state
    const [partySearchTerm, setPartySearchTerm] = useState('');
    const [showPartyDropdown, setShowPartyDropdown] = useState(false);

    // Filter states for parties list
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('All');
    const [filterGstType, setFilterGstType] = useState('All');
    const [filterBalance, setFilterBalance] = useState('All');
    
    // Contact Sync States
    const [showContactSelector, setShowContactSelector] = useState(false);
    const [syncingContacts, setSyncingContacts] = useState(false);
    const [syncStatus, setSyncStatus] = useState(null);
    
    const formSectionRef = useRef(null);
    
    // Loading state - set to TRUE immediately on mount, before any data operations
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    
    // Track if we've shown loading screen at least once (minimum display time)
    const [loadingStartTime, setLoadingStartTime] = useState(null);
    const MIN_LOADING_TIME = 500; // Minimum 500ms to show loading screen
    
    // Use loading context for better UX - MUST be defined before checkAndStopInitialLoading
    const { startLoading, stopLoading } = useComponentLoading('parties', 'Loading parties...');
    
    // Track which data sources have loaded
    const dataLoadedRef = useRef({
        parties: false
    });
    
    // Function to check if all required data is loaded and stop initial loading
    const checkAndStopInitialLoading = useCallback(() => {
        const { parties } = dataLoadedRef.current;
        if (parties) {
            const elapsed = loadingStartTime ? Date.now() - loadingStartTime : 0;
            const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsed);
            
            if (remainingTime > 0) {
                console.log(`🔄 [LOADING SCREEN] Parties: All data loaded, waiting ${remainingTime}ms more for minimum display time`);
                setTimeout(() => {
                    console.log('🔄 [LOADING SCREEN] Parties: Stopping initial loading - all data loaded and minimum time elapsed');
                    setIsInitialLoading(false);
                    stopLoading();
                }, remainingTime);
            } else {
                console.log('🔄 [LOADING SCREEN] Parties: Stopping initial loading - all data loaded');
                setIsInitialLoading(false);
                stopLoading();
            }
        }
    }, [loadingStartTime, stopLoading]);
    
    // Bulk delete states
    const [selectedForDeletion, setSelectedForDeletion] = useState(new Set());
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    // Bulk delete functions
    const handleDeleteSelection = (partyId) => {
        const newSelection = new Set(selectedForDeletion);
        if (newSelection.has(partyId)) {
            newSelection.delete(partyId);
        } else {
            newSelection.add(partyId);
        }
        setSelectedForDeletion(newSelection);
    };

    const handleSelectAllForDeletion = () => {
        if (selectedForDeletion.size === filteredParties.length && filteredParties.length > 0) {
            setSelectedForDeletion(new Set());
        } else {
            setSelectedForDeletion(new Set(filteredParties.map(party => party.id)));
        }
    };

    const handleBulkDelete = async () => {
        if (selectedForDeletion.size === 0) {
            warning('Please select parties to delete', { title: 'No Selection' });
            return;
        }

        setIsBulkDeleting(true);
        try {
            const partiesRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
            
            const deletePromises = Array.from(selectedForDeletion).map(partyId => {
                const partyRef = doc(partiesRef, partyId);
                return deleteDoc(partyRef);
            });

            await Promise.all(deletePromises);
            
            // 🔒 PHASE 3: Invalidate caches after bulk delete
            console.log('🧹 PHASE 3: Invalidating caches after bulk delete');
            secureCacheManager.invalidate(`parties_list_${companyId}`);
            secureCacheManager.invalidate(`dashboard_${companyId}`);
            
            setSelectedForDeletion(new Set());
            setShowBulkDeleteConfirm(false);
            
            success(`Successfully deleted ${selectedForDeletion.size} party(ies)`, { title: 'Parties Deleted', duration: 6000 });
        } catch (error) {
            handleError(error, { 
                context: 'PartiesBulkDelete',
                title: 'Delete Failed'
            });
        } finally {
            setIsBulkDeleting(false);
        }
    };

    // Contact Sync Functions
    const handleSyncContacts = async () => {
        if (!appId || !userId) return;
        
        setSyncingContacts(true);
        try {
            const result = await contactSyncService.syncContactsToDatabase(userId, appId);
            setSyncStatus(result);
            
            if (result.success) {
                console.log('Contacts synced successfully:', result.message);
            } else {
                console.error('Contact sync failed:', result.message);
            }
        } catch (error) {
            console.error('Error syncing contacts:', error);
            setSyncStatus({ success: false, message: 'Error syncing contacts' });
        } finally {
            setSyncingContacts(false);
        }
    };

    const handleSelectContact = (contact) => {
        // Pre-fill form with contact data
        setFirmName(contact.name);
        setPersonName(contact.name);
        
        if (contact.phones && contact.phones.length > 0) {
            setContact(contact.phones[0].number);
        }
        
        if (contact.emails && contact.emails.length > 0) {
            setEmail(contact.emails[0].address);
        }
        
        if (contact.organization) {
            setFirmName(contact.organization);
        }
        
        setShowContactSelector(false);
    };

    const getSyncStatus = async () => {
        try {
            const status = await contactSyncService.getSyncStatus();
            setSyncStatus(status);
        } catch (error) {
            console.error('Error getting sync status:', error);
        }
    };

    // Load sync status on component mount
    useEffect(() => {
        if (appId && userId) {
            getSyncStatus();
        }
    }, [appId, userId]);

    // Filter parties based on search term and filters
    const filteredParties = useMemo(() => {
        return partiesList.filter(party => {
            // Search term filter
            const matchesSearch = !searchTerm || 
                party.firmName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                party.personName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                party.contact?.includes(searchTerm) ||
                party.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                party.gstin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                party.address?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesType = partyMatchesTypeFilter(party, filterType);

            // GST type filter
            const matchesGstType = filterGstType === 'All' || party.gstType === filterGstType;

            // Balance filter
            const matchesBalance = filterBalance === 'All' || 
                (filterBalance === 'Receivable' && party.openingBalance > 0) ||
                (filterBalance === 'Payable' && party.openingBalance < 0) ||
                (filterBalance === 'No Balance' && party.openingBalance === 0);

            return matchesSearch && matchesType && matchesGstType && matchesBalance;
        });
    }, [partiesList, searchTerm, filterType, filterGstType, filterBalance]);

    const partyStats = useMemo(() => {
        const buyers = partiesList.filter((p) => p.partyType === 'Buyer').length;
        const suppliers = partiesList.filter((p) => isSupplierPartyType(p.partyType)).length;
        return { buyers, suppliers, total: partiesList.length };
    }, [partiesList]);

    // Monitor selectedParty state changes
    useEffect(() => {
        console.log('selectedParty state changed:', selectedParty);
    }, [selectedParty]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            console.log('Click outside detected, target:', event.target.id, event.target.className);
            // Check if the click is on a dropdown item
            if (event.target.closest('.dropdown-item')) {
                console.log('Click was on dropdown item, not closing dropdown yet');
                return;
            }
            if (!event.target.closest('#partySelect')) {
                console.log('Closing dropdown - clicked outside partySelect');
                setShowPartyDropdown(false);
            } else {
                console.log('Click was inside partySelect, keeping dropdown open');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Table sorting hook with default sort by creation date descending (LIFO)
    const { sortConfig, handleSort, getSortedData } = useTableSort([], { key: 'createdAt', direction: 'desc' });
    
    const sortedParties = getSortedData(filteredParties);

    const indianStates = [
        'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
        'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
        'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
        'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
        'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
        'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
        'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
        'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
    ];

    // GST State Code Mapping (First 2 digits of GSTIN)
    const gstStateCodes = {
        '01': 'Jammu and Kashmir',
        '02': 'Himachal Pradesh',
        '03': 'Punjab',
        '04': 'Chandigarh',
        '05': 'Uttarakhand',
        '06': 'Haryana',
        '07': 'Delhi',
        '08': 'Rajasthan',
        '09': 'Uttar Pradesh',
        '10': 'Bihar',
        '11': 'Sikkim',
        '12': 'Arunachal Pradesh',
        '13': 'Nagaland',
        '14': 'Manipur',
        '15': 'Mizoram',
        '16': 'Tripura',
        '17': 'Meghalaya',
        '18': 'Assam',
        '19': 'West Bengal',
        '20': 'Jharkhand',
        '21': 'Odisha',
        '22': 'Chhattisgarh',
        '23': 'Madhya Pradesh',
        '24': 'Gujarat',
        '25': 'Daman and Diu',
        '26': 'Dadra and Nagar Haveli',
        '27': 'Maharashtra',
        '28': 'Andhra Pradesh',
        '29': 'Karnataka',
        '30': 'Goa',
        '31': 'Lakshadweep',
        '32': 'Kerala',
        '33': 'Tamil Nadu',
        '34': 'Puducherry',
        '35': 'Andaman and Nicobar Islands',
        '36': 'Telangana',
        '37': 'Andhra Pradesh (New)'
    };

    // GSTIN validation regex (Indian GSTIN format)
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}[Z]{1}[A-Z0-9]{1}$/;
    // Indian phone number validation (10 digits, starts with 6-9)
    const phoneRegex = /^[6-9][0-9]{9}$/;
    // Indian PAN validation regex (5 letters + 4 digits + 1 letter)
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

    // Keep partyType in URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
        if (params.get('type') !== partyType) {
            params.set('type', partyType);
            const base = window.location.hash.split('?')[0] || '#/parties';
            const next = `${base}?${params.toString()}`;
            if (window.location.hash !== next) window.location.hash = next;
        }
    }, [partyType]);

    // Start loading IMMEDIATELY on mount and track start time - BEFORE any data operations
    useEffect(() => {
        console.log('🔄 [LOADING SCREEN] Parties: Component mounted - starting loading IMMEDIATELY (before any data operations)');
        const startTime = Date.now();
        setLoadingStartTime(startTime);
        setIsInitialLoading(true);
        startLoading('Loading Parties...', 'Fetching your customer and supplier data');
    }, [startLoading]);

    // Fetch parties data from Firestore
    useEffect(() => {
        if (db && userId && isAuthReady && appId && companyId) {
            // Reset data tracking when companyId changes
            dataLoadedRef.current = {
                parties: false
            };
            
            const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
            if (!partiesCollectionRef) {
                // Mark as loaded even if collection ref is null to prevent infinite loading
                dataLoadedRef.current.parties = true;
                checkAndStopInitialLoading();
                return;
            }
            
            const q = query(partiesCollectionRef);

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const parties = [];
                snapshot.forEach((doc) => {
                    const partyData = doc.data();
                    // Set default GST type for existing parties that don't have it
                    if (!partyData.gstType) {
                        partyData.gstType = 'Unregistered';
                        // Update the party with default GST type
                        setDoc(doc.ref, { gstType: 'Unregistered' }, { merge: true }).catch(console.error);
                    }
                    parties.push({ id: doc.id, ...partyData });
                });
                // LIFO sorting is now handled by the pagination utility
                setPartiesList(parties);
                
                // Mark parties as loaded and check if all data is ready
                if (!dataLoadedRef.current.parties) {
                    dataLoadedRef.current.parties = true;
                    console.log('🔄 [LOADING SCREEN] Parties: Parties data loaded, checking if all data is ready');
                    checkAndStopInitialLoading();
                }
            }, (error) => {
                console.error("Error fetching parties:", error);
                setMessage("Error fetching parties. Please try again.");
                // Mark as loaded even on error to prevent infinite loading
                if (!dataLoadedRef.current.parties) {
                    dataLoadedRef.current.parties = true;
                    checkAndStopInitialLoading();
                }
            });

            return () => {
                unsubscribe();
            };
        }
    }, [db, userId, isAuthReady, appId, companyId, checkAndStopInitialLoading]);

    useEffect(() => {
        if (!db || !userId || !isAuthReady || !companyId) return undefined;
        const tagsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'serviceTags');
        if (!tagsRef) return undefined;
        return onSnapshot(tagsRef, (snap) => {
            const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setServiceTagMaster(all);
            const names = dedupeServiceTagsForDisplay(all)
                .filter((t) => t.isActive !== false)
                .map((t) => normalizeTagName(t.normalizedName || t.name))
                .filter(Boolean);
            setServiceTagOptions(Array.from(new Set(names)).sort());
        });
    }, [db, userId, isAuthReady, appId, companyId]);

    // Fetch company details for GST state code matching
    useEffect(() => {
        if (db && isAuthReady && companyId) {
            const unsubscribe = onSnapshot(doc(db, 'companies', companyId), (docSnap) => {
                if (docSnap.exists()) {
                    setCompanyDetails(normalizeCompanyDocSnapshot(docSnap.data() || {}, companyId));
                } else {
                    setCompanyDetails(null);
                }
            }, (error) => {
                console.error('Error fetching company details:', error);
                setCompanyDetails(null);
            });
            return () => unsubscribe();
        }
    }, [db, isAuthReady, companyId]);

    useEffect(() => {
        if (sameAsWhatsapp) {
            setWhatsapp(contact);
        }
    }, [contact, sameAsWhatsapp]);

    // Debounced contact search when phone number is entered
    useEffect(() => {
        // Clear existing timer
        if (phoneDebounceTimer) {
            clearTimeout(phoneDebounceTimer);
        }

        // Only search if phone has at least 10 digits and not currently editing
        const normalized = normalizePhone(contact);
        if (normalized && normalized.length >= 10 && !editingPartyId && appId && userId) {
            setIsSearchingContact(true);
            
            const timer = setTimeout(async () => {
                try {
                    const matches = await searchContactByPhone(contact, userId, appId);
                    if (matches.length > 0) {
                        setContactSuggestion({
                            contact: matches[0],
                            confidence: matches[0].confidence || 95
                        });
                        info('Contact found in your phone! 📱', { title: 'Contact Found', duration: 5000 });
                    } else {
                        setContactSuggestion(null);
                    }
                } catch (error) {
                    console.error('Error searching contacts:', error);
                    setContactSuggestion(null);
                } finally {
                    setIsSearchingContact(false);
                }
            }, 500); // Wait 500ms after user stops typing
            
            setPhoneDebounceTimer(timer);
        } else {
            setIsSearchingContact(false);
            if (editingPartyId) {
                setContactSuggestion(null); // Don't show suggestions when editing
            }
        }

        return () => {
            if (phoneDebounceTimer) {
                clearTimeout(phoneDebounceTimer);
            }
        };
    }, [contact, editingPartyId, appId, userId]);

    // Handle accepting contact suggestion
    const handleAcceptContactSuggestion = (suggestedContact) => {
        if (!suggestedContact) return;
        
        // Auto-fill form fields from contact
        if (suggestedContact.name) setFirmName(suggestedContact.name);
        if (suggestedContact.name) setPersonName(suggestedContact.name);
        if (suggestedContact.phones && suggestedContact.phones.length > 0) {
            setContact(suggestedContact.phones[0].number);
            // Also set WhatsApp to same number by default
            if (!whatsapp) {
                setWhatsapp(suggestedContact.phones[0].number);
                setSameAsWhatsapp(true);
            }
        }
        if (suggestedContact.emails && suggestedContact.emails.length > 0) {
            setEmail(suggestedContact.emails[0].address);
        }
        if (suggestedContact.organization) {
            // Use organization name as firm name if not already filled
            if (!firmName) setFirmName(suggestedContact.organization);
        }
        
        // Clear suggestion
        setContactSuggestion(null);
        
        // Show success message
        success('Contact details auto-filled! Please add GST and address.', {
            title: 'Contact Auto-filled',
            duration: 5000
        });
    };

    // Handle rejecting contact suggestion
    const handleRejectContactSuggestion = () => {
        setContactSuggestion(null);
        info('Suggestion ignored. Continue entering manually.', { title: 'Suggestion Ignored', duration: 5000 });
    };

    // Function to clear party form fields
    const clearPartyForm = () => {
        setFirmName('');
        setPersonName('');
        setAddress('');
        setContact('');
        setEmail('');
        setWhatsapp('');
        setGstin('');
        setGstType('Unregistered');
        setPan('');
        setCity('');
        setStateName('');
        setPincode('');
        setCreditPeriod('0');
        setCreditLimit('0');
        setOpeningBalance('0');
        setPartyType('Buyer');
        setIsServiceVendor(false);
        setVendorServiceTags([]);
        setEditingPartyId(null);
        setMessage('');
        setContactSuggestion(null); // Also clear contact suggestion
        setSameAsWhatsapp(false);

        // Reset GST validation state
        setGstValidation({
            isValid: null,
            message: '',
            stateInfo: null,
            panInfo: null
        });
        setManualBalance('0');
        setManualBalanceDate('');
        setManualBalanceRemarks('');
        setUseManualBalance(true);
    };

    // Function to handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!db || !userId) {
            setMessage("Firebase not initialized or user not authenticated.");
            return;
        }

        // Check email verification before allowing party creation
        const { checkActionRequiresVerification } = await import('../utils/emailVerificationCheck');
        const verificationCheck = await checkActionRequiresVerification('create or edit parties', {
            requireEmail: true,
            allowPhone: true,
            allowGoogle: true
        });
        
        if (verificationCheck.needsVerification) {
            const verificationError = new Error(verificationCheck.message);
            verificationError.code = 'VERIFICATION_REQUIRED';
            handleValidationError(verificationError, {
                title: 'Email Verification Required',
                context: 'Party Creation',
                severity: 'warning',
                duration: 10000
            });
            // Show additional warning notification with action
            showWarning(verificationCheck.message + ' Please verify your account to continue.', {
                title: 'Verification Required',
                duration: 10000
            });
            // Also show info about how to verify
            info('Go to Settings or click the verification link in your email to verify your account.', {
                title: 'How to Verify',
                duration: 8000
            });
            return;
        }

        // Validate required fields
        if (!firmName.trim()) {
            setMessage("Party name is required.");
            return;
        }

        // GST validation for Regular parties
        if (gstType === 'Regular') {
            if (!gstin.trim()) {
                setMessage("GSTIN is required for Regular parties.");
                return;
            }
            if (gstValidation.isValid === false) {
                setMessage("Please enter a valid GST number. " + gstValidation.message);
                return;
            }
            if (gstValidation.isValid !== true) {
                setMessage("Please wait for GST validation to complete.");
                return;
            }
        }

        // GST validation for other types (if GSTIN is provided)
        if (gstin.trim() && gstValidation.isValid === false) {
            setMessage("Please enter a valid GST number. " + gstValidation.message);
            return;
        }

        // Validate that party GSTIN and company GSTIN have matching state codes (first 2 digits)
        if (gstin.trim() && companyDetails?.gstin) {
            const partyStateCode = gstin.trim().substring(0, 2);
            const companyStateCode = companyDetails.gstin.trim().substring(0, 2);
            
            if (partyStateCode !== companyStateCode) {
                setMessage(`Party GSTIN state code (${partyStateCode}) does not match company GSTIN state code (${companyStateCode}). Both must be from the same state.`);
                return;
            }
        }

        // Additional validation for editing mode
        if (editingPartyId) {
            console.log('Validating edit mode - editingPartyId:', editingPartyId);
            if (!editingPartyId || editingPartyId === 'null' || editingPartyId === 'undefined') {
                setMessage("Invalid party ID for editing. Please try again.");
                return;
            }
        }

        // GST validation is now handled by the GSTInput component
        // State validation will be done automatically based on GST validation

        // Validate phone number format if provided
        if (contact.trim() && !phoneRegex.test(contact.trim())) {
            setMessage("Invalid phone number format. Please enter a valid 10-digit Indian mobile number.");
            return;
        }

        // Validate PAN format if provided
        if (pan.trim() && !panRegex.test(pan.trim())) {
            setMessage("Invalid PAN format. Please check and try again.");
            return;
        }

        try {
            // Validate that we have the required IDs for editing
            if (editingPartyId && (!editingPartyId.trim() || editingPartyId === 'null' || editingPartyId === 'undefined')) {
                setMessage("Invalid party ID for editing. Please try again.");
                return;
            }

            const partyData = {
                firmName: firmName.trim(),
                personName: personName.trim(),
                address: address.trim(),
                contact: contact.trim(),
                email: email.trim(),
                whatsapp: whatsapp.trim(),
                gstin: gstin.trim(),
                gstType: gstType, // Add GST type
                pan: pan.trim(),
                city: city.trim(),
                stateName: stateName.trim(),
                pincode: pincode.trim(),
                creditPeriod: parseInt(creditPeriod) || 0,
                creditLimit: parseFloat(creditLimit) || 0,
                openingBalance: parseFloat(openingBalance) || 0,
                partyType: partyType,
                isServiceVendor: isServiceVendor || partyType === 'Seller' || partyType === 'Both',
                vendorServiceTags: normalizeVendorServiceTags(vendorServiceTags),
                updatedAt: serverTimestamp(),
                // New fields for opening balance management
                useManualBalance: useManualBalance,
                manualBalance: parseFloat(manualBalance) || 0,
                manualBalanceDate: manualBalanceDate || new Date().toISOString().split('T')[0],
                manualBalanceRemarks: manualBalanceRemarks.trim()
            };

            // Only add createdAt for new parties
            if (!editingPartyId) {
                partyData.createdAt = serverTimestamp();
            }

            // Log the data being sent for debugging
            console.log('Saving party data:', { editingPartyId, partyData });

            // Validate that all required fields are present and not undefined
            const requiredFields = ['firmName', 'personName', 'address', 'contact', 'email', 'whatsapp', 'city', 'stateName', 'pincode'];
            for (const field of requiredFields) {
                if (partyData[field] === undefined || partyData[field] === null) {
                    console.error(`Missing required field: ${field}`);
                    warning(`Missing required field: ${field}. Please try again.`, { title: 'Validation Error' });
                    return;
                }
            }

            const companyCtx = { companyId, appId, userId };
            const recordOpeningBalancePayment = async (partyId, partyName, amount, partyTypeValue, isNewParty) => {
                try {
                    await createOpeningBalancePaymentEntry(db, companyCtx, {
                        partyId,
                        partyName,
                        openingBalanceAmount: amount,
                        partyTypeValue,
                        isNewParty,
                    });
                } catch (paymentErr) {
                    console.error('Error creating opening balance payment entry:', paymentErr);
                    warning('Party saved successfully, but failed to create opening balance payment entry.');
                }
            };

            if (editingPartyId) {
                // Update existing party
                console.log('Updating party with ID:', editingPartyId);
                const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
                if (!partiesCollectionRef) {
                    error("Error: Unable to access parties collection.", { title: 'Access Error', duration: 7000 });
                    return;
                }
                
                // Get old party data to compare opening balance
                const partyRef = doc(partiesCollectionRef, editingPartyId);
                const oldPartyDoc = await getDoc(partyRef);
                let oldOpeningBalance = 0;
                let oldPartyName = firmName;
                
                if (oldPartyDoc.exists()) {
                    const oldPartyData = oldPartyDoc.data();
                    oldOpeningBalance = oldPartyData.openingBalance || 0;
                    oldPartyName = oldPartyData.firmName || firmName;
                }
                
                const newOpeningBalance = parseFloat(openingBalance) || 0;
                const openingBalanceChanged = newOpeningBalance !== oldOpeningBalance;
                
                await setDoc(partyRef, partyData, { merge: true });
                
                // Create payment entry if opening balance changed
                if (openingBalanceChanged) {
                    const openingBalanceDifference = newOpeningBalance - oldOpeningBalance;
                    if (openingBalanceDifference !== 0) {
                        await recordOpeningBalancePayment(
                            editingPartyId,
                            firmName,
                            openingBalanceDifference,
                            partyType,
                            false
                        );
                    }
                }
                
                // 🔒 PHASE 3: Invalidate caches after update
                console.log('🧹 PHASE 3: Invalidating caches after party update');
                secureCacheManager.invalidate(`parties_list_${companyId}`);
                secureCacheManager.invalidate(`dashboard_${companyId}`);
                
                success(
                    `Party ${firmName} updated successfully!`,
                    {
                        title: 'Party Updated',
                        details: `${partyType} • ${gstType} • ${contact || 'No contact'}`,
                        duration: 6000
                    }
                );
                console.log('Party updated successfully');
            } else {
                // Add new party
                console.log('Adding new party');
                const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
                if (!partiesCollectionRef) {
                    error("Error: Unable to access parties collection.", { title: 'Access Error', duration: 7000 });
                    return;
                }
                const newPartyRef = await addDoc(partiesCollectionRef, partyData);
                const newPartyId = newPartyRef.id;
                
                // Create payment entry for opening balance if it exists
                const openingBalanceAmount = parseFloat(openingBalance) || 0;
                if (openingBalanceAmount !== 0) {
                    await recordOpeningBalancePayment(newPartyId, firmName, openingBalanceAmount, partyType, true);
                }
                
                // 🔒 PHASE 3: Invalidate caches after adding new party
                console.log('🧹 PHASE 3: Invalidating caches after party add');
                secureCacheManager.invalidate(`parties_list_${companyId}`);
                secureCacheManager.invalidate(`dashboard_${companyId}`);
                
                success(
                    `Party ${firmName} added successfully!`,
                    {
                        title: 'Party Added',
                        details: `${partyType} • ${gstType} • ${contact || 'No contact'}`,
                        duration: 6000
                    }
                );
                console.log('Party added successfully');
            }
            
            // Clear form and reset editing state
            clearPartyForm();
            console.log('Form cleared and editing state reset');
        } catch (err) {
            console.error("Error saving party:", err);
            
            // 🚫 Handle subscription/limit errors
            if (err.code === 'SUBSCRIPTION_EXPIRED' || err.code === 'FEATURE_LIMIT_EXCEEDED') {
                console.log('🚫 Subscription expired or limit exceeded:', err);
                // Error notification is already shown by ErrorNotificationService
                return;
            }
            
            // Provide more specific error messages
            if (err.code === 'permission-denied') {
                error("Permission denied. Please check your authentication.", { title: 'Permission Denied', duration: 7000 });
            } else if (err.code === 'unavailable') {
                error("Service temporarily unavailable. Please try again.", { title: 'Service Unavailable', duration: 7000 });
            } else if (err.message && err.message.includes('invalid data')) {
                error("Invalid data format. Please check all fields and try again.", { title: 'Validation Error', duration: 7000 });
            } else {
                error(`Error saving party: ${err.message || 'Please try again.'}`, { title: 'Save Failed', duration: 7000 });
            }
        }
    };

    // Handle company details auto-fill from GST
    const handleCompanyDetailsFetch = (companyDetails) => {
        if (!companyDetails) return;

        // Auto-fill form fields with fetched data
        setFirmName(companyDetails.companyName || firmName);
        setAddress(companyDetails.address || address);
        setCity(companyDetails.city || city);
        setStateName(companyDetails.state || stateName);
        setPincode(companyDetails.pincode || pincode);
        setContact(companyDetails.contact || contact);
        setEmail(companyDetails.email || email);
        setPan(companyDetails.pan || pan);

        // Show success message
        setMessage(`✅ Company details auto-filled! You can still edit these fields if needed.`);

        // Auto-scroll to show the filled fields
        setTimeout(() => {
            const formElement = document.querySelector('#party-entry-form');
            if (formElement) {
                formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 300);
    };

    // Function to handle editing a party
    const handleEditParty = (party) => {
        // Validate party object
        if (!party || !party.id) {
            console.error('Invalid party object for editing:', party);
            setMessage('Invalid party data for editing. Please try again.');
            return;
        }

        console.log('Editing party:', party);

        setEditingPartyId(party.id);
        setFirmName(party.firmName || '');
        setPersonName(party.personName || '');
        setAddress(party.address || '');
        setCity(party.city || '');
        setStateName(party.stateName || '');
        setPincode(party.pincode || '');
        setContact(party.contact || '');
        setEmail(party.email || '');
        setWhatsapp(party.whatsapp || '');
        setGstin(party.gstin || '');
        setGstType(party.gstType || 'Unregistered'); // Set GST type
        setPan(party.pan || '');
        setCreditPeriod(party.creditPeriod?.toString() || '0');
        setCreditLimit(party.creditLimit?.toString() || '0');
        setOpeningBalance(party.openingBalance?.toString() || '0');
        setPartyType(party.partyType || 'Buyer');
        setIsServiceVendor(isPartyServiceVendor(party));
        setVendorServiceTags(normalizeVendorServiceTags(party.vendorServiceTags));
        setMessage('Editing existing party.');
        setSameAsWhatsapp(party.whatsapp === party.contact);

        // Set opening balance management fields
        setUseManualBalance(party.useManualBalance !== false);
        setManualBalance(party.manualBalance?.toString() || '0');
        setManualBalanceDate(party.manualBalanceDate || '');
        setManualBalanceRemarks(party.manualBalanceRemarks || '');

        // Set calculation date to current date if not set
        if (!party.manualBalanceDate) {
            setManualBalanceDate(new Date().toISOString().split('T')[0]);
        }

        // Reset GST validation state for editing
        setGstValidation({
            isValid: null,
            message: '',
            stateInfo: null,
            panInfo: null
        });

        setActiveTab('information');
        setTimeout(() => {
            formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
    };

    // Handle deleting a party
    const handleDeleteParty = async (partyId) => {
        if (!db || !userId) {
            setMessage("Firebase not initialized or user not authenticated.");
            return;
        }
        
        try {
            // Check if party is referenced in any bills
            const party = partiesList.find(p => p.id === partyId);
            if (!party) {
                setMessage("Party not found.");
                return;
            }
            
            // Check for references in sales bills
            const salesBillsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills');
            const salesBillsSnap = await getDocs(query(salesBillsRef, where('party', '==', partyId)));
            
            // Check for references in purchase bills
            const purchaseBillsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills');
            const purchaseBillsSnap = await getDocs(query(purchaseBillsRef, where('party', '==', partyId)));
            
            // Check for references in challans
            const challansRef = getCollectionForCompany(db, { companyId, appId, userId }, 'challans');
            const challansSnap = await getDocs(query(challansRef, where('party', '==', partyId)));
            
            // Check for references in quotations
            const quotationsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'quotations');
            const quotationsSnap = await getDocs(query(quotationsRef, where('party', '==', partyId)));
            
            const totalReferences = salesBillsSnap.size + purchaseBillsSnap.size + challansSnap.size + quotationsSnap.size;
            
            if (totalReferences > 0) {
                setMessage(`Cannot delete party "${party.firmName}". This party is referenced in ${totalReferences} bill(s). Please delete the related bills first.`);
                return;
            }
            
            // Delete the party
            const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
            if (!partiesCollectionRef) {
                setMessage("Error: Unable to access parties collection.");
                return;
            }
            const partyRef = doc(partiesCollectionRef, partyId);
            await deleteDoc(partyRef);
            
            // Get party details before deleting for notification
            const partyToDelete = partiesList.find(p => p.id === partyId);
            const partyName = partyToDelete?.firmName || 'Unknown Party';
            const partyTypeForDelete = partyToDelete?.partyType || 'Unknown';
            
            // 🔒 PHASE 3: Invalidate caches after delete
            console.log('🧹 PHASE 3: Invalidating caches after party delete');
            secureCacheManager.invalidate(`parties_list_${companyId}`);
            secureCacheManager.invalidate(`dashboard_${companyId}`);
            
            success(
                `Party ${partyName} deleted successfully!`,
                {
                    title: 'Party Deleted',
                    details: `${partyName} • ${partyTypeForDelete}`,
                    duration: 6000
                }
            );
            
            // Clear selected party if it was the deleted one
            if (selectedParty && selectedParty.id === partyId) {
                setSelectedParty(null);
            }
            
        } catch (err) {
            console.error("Error deleting party:", err);
            
            // 🚫 Handle subscription/limit errors
            if (err.code === 'SUBSCRIPTION_EXPIRED' || err.code === 'FEATURE_LIMIT_EXCEEDED') {
                console.log('🚫 Subscription expired or limit exceeded:', err);
                // Error notification is already shown by ErrorNotificationService
                return;
            }
            
            if (err.code === 'permission-denied') {
                error("Permission denied. You don't have access to delete this party.", { title: 'Permission Denied', duration: 7000 });
            } else if (err.code === 'not-found') {
                warning("Party not found. It may have been already deleted.", { title: 'Party Not Found' });
            } else {
                error(`Error deleting party: ${err.message}`, { title: 'Delete Failed', duration: 7000 });
            }
        }
    };

    // Function to select party for detailed view
    const handleSelectParty = (party) => {
        setSelectedParty(party);
        setPartySearchTerm(party.firmName || '');
        setActiveTab('outstandings');
        
        // Reset calculated outstanding (will be recalculated)
        setCalculatedOutstanding(null);
        
        // Sync manual balance fields
        setManualBalance(party.openingBalance?.toString() || '0');
        setManualBalanceDate(party.manualBalanceDate || new Date().toISOString().split('T')[0]);
        setManualBalanceRemarks(party.manualBalanceRemarks || '');
        setUseManualBalance(party.useManualBalance !== false);
        
        // Load transaction data for the selected party
        fetchPartyTransactions(party.id);
        calculatePartyAging(party.id);
        
        // Calculate outstanding with bills data
        calculateOutstandingAsync(party);
    };

    // Function to calculate outstanding for a party (async - fetches bills and payments)
    const calculateOutstandingAsync = async (party) => {
        if (!party || !db || !userId || !appId || !companyId) {
            return party?.openingBalance || 0;
        }
        
        setIsCalculatingOutstanding(true);
        try {
            // Fetch payments once (used for both sales and purchase calculations)
            const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
            const paymentsSnapshot = await getDocs(paymentsRef);
            const payments = [];
            paymentsSnapshot.forEach(doc => payments.push({ id: doc.id, ...doc.data() }));
            
            // Build payment allocation map for quick lookup
            const paymentsByBill = new Map();
            payments.forEach(payment => {
                if (payment.allocations && Array.isArray(payment.allocations)) {
                    payment.allocations.forEach(alloc => {
                        const key = `${alloc.billId}_${alloc.billType}`;
                        const current = paymentsByBill.get(key) || 0;
                        paymentsByBill.set(key, current + (parseFloat(alloc.allocatedAmount) || 0));
                    });
                }
            });
            
            // Start with opening balance components
            let receivable = party.openingBalance > 0 ? party.openingBalance : 0;
            let payable = party.openingBalance < 0 ? Math.abs(party.openingBalance) : 0;
            
            // Fetch sales bills for this party
            let salesOutstanding = 0;
            try {
                const salesBillsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills');
                const salesSnapshot = await getDocs(query(salesBillsRef, where('partyId', '==', party.id)));
                
                // Also check 'party' field for backward compatibility
                let salesSnapshot2;
                try {
                    salesSnapshot2 = await getDocs(query(salesBillsRef, where('party', '==', party.id)));
                } catch (e) {
                    // Ignore if query fails
                }
                
                const salesBills = [];
                salesSnapshot.forEach(doc => salesBills.push({ id: doc.id, ...doc.data() }));
                if (salesSnapshot2) {
                    salesSnapshot2.forEach(doc => {
                        if (!salesBills.find(b => b.id === doc.id)) {
                            salesBills.push({ id: doc.id, ...doc.data() });
                        }
                    });
                }
                
                // Calculate outstanding for each sales bill
                salesBills.forEach(bill => {
                    const totalAmount = parseFloat(bill.totalAmount || bill.amount || 0);
                    // Check both invoice and challan bill types
                    const paid = (paymentsByBill.get(`${bill.id}_invoice`) || 0) + 
                                 (paymentsByBill.get(`${bill.id}_challan`) || 0);
                    const outstanding = totalAmount - paid;
                    if (outstanding > 0) {
                        salesOutstanding += outstanding;
                    }
                });
            } catch (error) {
                console.warn('Error fetching sales bills for outstanding calculation:', error);
            }
            
            // Fetch purchase bills for this party
            let purchaseOutstanding = 0;
            try {
                const purchaseBillsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills');
                const purchaseSnapshot = await getDocs(query(purchaseBillsRef, where('partyId', '==', party.id)));
                
                // Also check 'party' field for backward compatibility
                let purchaseSnapshot2;
                try {
                    purchaseSnapshot2 = await getDocs(query(purchaseBillsRef, where('party', '==', party.id)));
                } catch (e) {
                    // Ignore if query fails
                }
                
                const purchaseBills = [];
                purchaseSnapshot.forEach(doc => purchaseBills.push({ id: doc.id, ...doc.data() }));
                if (purchaseSnapshot2) {
                    purchaseSnapshot2.forEach(doc => {
                        if (!purchaseBills.find(b => b.id === doc.id)) {
                            purchaseBills.push({ id: doc.id, ...doc.data() });
                        }
                    });
                }
                
                // Calculate outstanding for each purchase bill
                purchaseBills.forEach(bill => {
                    const totalAmount = parseFloat(bill.totalAmount || bill.amount || 0);
                    const paid = paymentsByBill.get(`${bill.id}_purchase`) || 0;
                    const outstanding = totalAmount - paid;
                    if (outstanding > 0) {
                        purchaseOutstanding += outstanding;
                    }
                });
            } catch (error) {
                console.warn('Error fetching purchase bills for outstanding calculation:', error);
            }
            
            // Calculate net outstanding (matching Dashboard logic)
            // Receivable: positive opening balance + sales outstanding
            receivable += salesOutstanding;
            // Payable: negative opening balance (as absolute) + purchase outstanding
            payable += purchaseOutstanding;
            
            // Net outstanding = receivable - payable
            // Positive = customer owes company, Negative = company owes supplier
            const netOutstanding = receivable - payable;
            
            setCalculatedOutstanding(netOutstanding);
            return netOutstanding;
            
        } catch (error) {
            console.error('Error calculating outstanding:', error);
            // Fallback to opening balance on error
            const fallback = party.openingBalance || 0;
            setCalculatedOutstanding(fallback);
            return fallback;
        } finally {
            setIsCalculatingOutstanding(false);
        }
    };
    
    // Function to calculate outstanding for a party (synchronous - returns cached or opening balance)
    // This function is used in the UI. For full calculation, call calculateOutstandingAsync first.
    const calculateOutstanding = (party) => {
        if (!party) return 0;
        
        // If we have a calculated outstanding for the selected party, use it
        if (selectedParty && selectedParty.id === party.id && calculatedOutstanding !== null) {
            return calculatedOutstanding;
        }
        
        // Otherwise, fallback to opening balance
        // Full calculation should be triggered via calculateOutstandingAsync when party is selected
        return party.openingBalance || 0;
    };

    // Function to get current financial year
    const getCurrentFinancialYear = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;
        if (month >= 4) {
            return `${year}-${(year + 1).toString().slice(-2)}`;
        } else {
            return `${year - 1}-${year.toString().slice(-2)}`;
        }
    };

    // Function to get previous financial year
    const getPreviousFinancialYear = () => {
        const currentYear = getCurrentFinancialYear();
        const startYear = parseInt(currentYear.split('-')[0]);
        return `${startYear - 1}-${startYear.toString().slice(-2)}`;
    };

    const calculatePartyOpeningBalance = async (party, targetYear) => {
        try {
            return await calculatePartyFyTotals(db, { companyId, appId, userId }, party, targetYear);
        } catch (error) {
            console.error('Error calculating opening balance for party:', party?.firmName, error);
            return null;
        }
    };

    // Function to calculate opening balances for all parties
    const calculateAllOpeningBalances = async () => {
        if (!db || !companyId) return;
        
        setIsCalculating(true);
        setMessage('Calculating opening balances...');
        const currentYear = getCurrentFinancialYear();
        console.log(`🔍 Starting opening balance calculation for FY: ${currentYear}`);
        console.log(`📅 Date range: April 1, ${currentYear.split('-')[0]} to March 31, ${parseInt(currentYear.split('-')[0]) + 1}`);
        
        try {
            const calculations = [];
            let processedCount = 0;
            
            for (const party of partiesList) {
                try {
                    const calculation = await calculatePartyOpeningBalance(party, currentYear);
                    if (calculation) {
                        calculations.push({
                            partyId: party.id,
                            partyName: party.firmName,
                            partyType: party.partyType,
                            ...calculation
                        });
                    }
                    processedCount++;
                    
                    // Update progress message
                    if (processedCount % 5 === 0 || processedCount === partiesList.length) {
                        setMessage(`Calculating opening balances... ${processedCount}/${partiesList.length} parties processed`);
                    }
                } catch (error) {
                    console.error(`Error calculating for party ${party.firmName}:`, error);
                    // Continue with other parties even if one fails
                }
            }
            
            if (calculations.length === 0) {
                setMessage('No calculations could be completed. Please check your data and try again.');
                return;
            }
            
            setCalculationData(calculations);
            setFinancialYear(currentYear);
            setShowCalculationPreview(true);
            setMessage(`Successfully calculated opening balances for ${calculations.length} parties!`);
            
            // Add to audit trail
            const newAuditEntry = {
                id: Date.now().toString(),
                action: 'Opening Balance Calculation',
                financialYear: currentYear,
                calculationDate: calculationDate,
                partiesCalculated: calculations.length,
                timestamp: new Date().toISOString(),
                details: `Calculated opening balances for ${calculations.length} parties for FY ${currentYear}`
            };
            
            setAuditTrail(prev => [newAuditEntry, ...prev.slice(0, 49)]);
            
        } catch (error) {
            console.error('Error calculating opening balances:', error);
            setMessage('Error calculating opening balances. Please try again.');
        } finally {
            setIsCalculating(false);
        }
    };

    // Function to apply calculated opening balances
    const applyCalculatedOpeningBalances = async () => {
        if (!db || !companyId || calculationData.length === 0) return;
        
        // Show confirmation dialog
        const confirmed = window.confirm(
            `Are you sure you want to apply the calculated opening balances for ${calculationData.length} parties?\n\n` +
            `This will update the opening balance field for all parties and cannot be undone automatically.\n\n` +
            `Financial Year: ${financialYear}\n` +
            `Calculation Date: ${calculationDate}\n\n` +
            `Click OK to proceed or Cancel to review further.`
        );
        
        if (!confirmed) return;
        
        try {
            setMessage('Applying calculated opening balances...');
            const batch = [];
            
            for (const calc of calculationData) {
                const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
                if (!partiesCollectionRef) {
                    setMessage("Error: Unable to access parties collection.");
                    return;
                }
                const partyRef = doc(partiesCollectionRef, calc.partyId);
                batch.push(setDoc(partyRef, {
                    openingBalance: calc.calculatedBalance,
                    lastCalculationDate: calc.calculationDate,
                    previousOpeningBalance: calc.previousBalance,
                    calculationRemarks: `Auto-calculated for FY ${financialYear}`,
                    updatedAt: serverTimestamp()
                }, { merge: true }));
            }
            
            await Promise.all(batch);
            
            // Update local state
            setPartiesList(prev => prev.map(party => {
                const calc = calculationData.find(c => c.partyId === party.id);
                if (calc) {
                    return {
                        ...party,
                        openingBalance: calc.calculatedBalance,
                        lastCalculationDate: calc.calculationDate
                    };
                }
                return party;
            }));
            
            // Add to audit trail
            const newAuditEntry = {
                id: Date.now().toString(),
                action: 'Opening Balance Applied',
                financialYear: financialYear,
                applicationDate: calculationDate,
                partiesUpdated: calculationData.length,
                timestamp: new Date().toISOString(),
                details: `Applied calculated opening balances for ${calculationData.length} parties for FY ${financialYear}`
            };
            
            setAuditTrail(prev => [newAuditEntry, ...prev.slice(0, 49)]);
            
            secureCacheManager.invalidate(`parties_list_${companyId}`);
            secureCacheManager.invalidate(`dashboard_${companyId}`);

            setMessage(`Successfully updated opening balances for ${calculationData.length} parties!`);
            setShowCalculationPreview(false);
            setCalculationData([]);
            
        } catch (error) {
            console.error('Error applying opening balances:', error);
            
            // 🚫 Handle subscription/limit errors
            if (error.code === 'SUBSCRIPTION_EXPIRED' || error.code === 'FEATURE_LIMIT_EXCEEDED') {
                console.log('🚫 Subscription expired or limit exceeded:', error);
                // Error notification is already shown by ErrorNotificationService
                return;
            }
            
            setMessage('Error applying opening balances. Please try again.');
        }
    };

    // Function to update manual balance for selected party
    const updateManualBalance = async () => {
        if (!selectedParty || !db || !companyId) return;
        
        try {
            const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
            if (!partiesCollectionRef) {
                setMessage("Error: Unable to access parties collection.");
                return;
            }
            const newBalance = parseFloat(manualBalance) || 0;
            const oldBalance = parseFloat(selectedParty.openingBalance) || 0;
            const balanceDiff = newBalance - oldBalance;

            const partyRef = doc(partiesCollectionRef, selectedParty.id);
            await setDoc(partyRef, {
                openingBalance: newBalance,
                manualBalance: newBalance,
                manualBalanceDate: manualBalanceDate || new Date().toISOString().split('T')[0],
                manualBalanceRemarks: manualBalanceRemarks.trim(),
                useManualBalance: true,
                updatedAt: serverTimestamp()
            }, { merge: true });

            if (balanceDiff !== 0) {
                try {
                    await createOpeningBalancePaymentEntry(db, { companyId, appId, userId }, {
                        partyId: selectedParty.id,
                        partyName: selectedParty.firmName,
                        openingBalanceAmount: balanceDiff,
                        partyTypeValue: selectedParty.partyType,
                        isNewParty: false,
                    });
                } catch (paymentErr) {
                    console.error('Manual balance payment entry failed:', paymentErr);
                    warning('Balance saved on party, but ledger entry could not be created.');
                }
            }

            secureCacheManager.invalidate(`parties_list_${companyId}`);
            secureCacheManager.invalidate(`dashboard_${companyId}`);
            
            const updatedParty = { ...selectedParty, openingBalance: newBalance };
            setSelectedParty(updatedParty);
            setPartiesList(prev => prev.map(party => 
                party.id === selectedParty.id ? { ...party, openingBalance: newBalance } : party
            ));
            
            const newAuditEntry = {
                id: Date.now().toString(),
                action: 'Manual Balance Update',
                financialYear: financialYear,
                calculationDate: manualBalanceDate,
                partiesUpdated: 1,
                timestamp: new Date().toISOString(),
                details: `Manually updated opening balance for ${selectedParty.firmName} to ₹${newBalance.toLocaleString('en-IN')}`
            };
            
            setAuditTrail(prev => [newAuditEntry, ...prev.slice(0, 49)]);
            
            success('Manual balance updated successfully!', { title: 'Balance Updated', duration: 5000 });
            
        } catch (error) {
            console.error('Error updating manual balance:', error);
            
            // 🚫 Handle subscription/limit errors
            if (error.code === 'SUBSCRIPTION_EXPIRED' || error.code === 'FEATURE_LIMIT_EXCEEDED') {
                console.log('🚫 Subscription expired or limit exceeded:', error);
                // Error notification is already shown by ErrorNotificationService
                return;
            }
            
            setMessage('Error updating manual balance. Please try again.');
        }
    };

    // Function to export calculation data to CSV
    const exportCalculationData = () => {
        if (calculationData.length === 0) return;
        
        const headers = [
            'Party Name',
            'Party Type',
            'Previous Balance',
            'Total Sales',
            'Total Purchases',
            'Payment Received (PRI)',
            'Payment Paid (PRP)',
            'Calculated Balance',
            'Calculation Date'
        ];
        
        const csvContent = [
            headers.join(','),
            ...calculationData.map(calc => [
                `"${calc.partyName}"`,
                calc.partyType,
                calc.previousBalance,
                calc.totalSales,
                calc.totalPurchases,
                calc.totalPayments,
                calc.totalPurchasePayments,
                calc.calculatedBalance,
                calc.calculationDate
            ].join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `opening_balance_calculation_${financialYear}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Function to export audit trail to CSV
    const exportAuditTrail = () => {
        if (auditTrail.length === 0) return;
        
        const headers = [
            'Action',
            'Financial Year',
            'Calculation Date',
            'Parties Updated',
            'Timestamp',
            'Details'
        ];
        
        const csvContent = [
            headers.join(','),
            ...auditTrail.map(entry => [
                `"${entry.action}"`,
                entry.financialYear || '',
                entry.calculationDate || '',
                entry.partiesUpdated || '',
                entry.timestamp,
                `"${entry.details}"`
            ].join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `audit_trail_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Function to clear audit trail
    const clearAuditTrail = () => {
        if (window.confirm('Are you sure you want to clear the audit trail? This action cannot be undone.')) {
            setAuditTrail([]);
            localStorage.removeItem(`auditTrail_${userId}_${appId}`);
            setMessage('Audit trail cleared successfully!');
        }
    };

    // Function to fetch party transactions (sales, purchases, payments)
    const fetchPartyTransactions = async (partyId) => {
        if (!db || !userId || !appId || !partyId) return;
        
        setIsLoadingTransactions(true);
        try {
            const transactions = [];
            
            // Fetch sales bills
            try {
                const salesQuery = query(
                    collection(db, `artifacts/${appId}/users/${userId}/sales`),
                    where('customerId', '==', partyId),
                    orderBy('billDate', 'desc'),
                    limit(50)
                );
                const salesSnapshot = await getDocs(salesQuery);
                salesSnapshot.forEach(doc => {
                    const data = doc.data();
                    transactions.push({
                        id: doc.id,
                        type: 'Sale',
                        date: data.billDate,
                        amount: data.totalAmount || 0,
                        balance: data.balanceAmount || 0,
                        billNumber: data.billNumber,
                        status: data.balanceAmount > 0 ? 'Outstanding' : 'Paid',
                        category: 'Sales'
                    });
                });
            } catch (error) {
                console.log('No sales data found for party:', error.message);
            }
            
            // Fetch purchase bills
            try {
                const purchaseQuery = query(
                    collection(db, `artifacts/${appId}/users/${userId}/purchases`),
                    where('supplierId', '==', partyId),
                    orderBy('billDate', 'desc'),
                    limit(50)
                );
                const purchaseSnapshot = await getDocs(purchaseQuery);
                purchaseSnapshot.forEach(doc => {
                    const data = doc.data();
                    transactions.push({
                        id: doc.id,
                        type: 'Purchase',
                        date: data.billDate,
                        amount: data.totalAmount || 0,
                        balance: data.balanceAmount || 0,
                        billNumber: data.billNumber,
                        status: data.balanceAmount > 0 ? 'Outstanding' : 'Paid',
                        category: 'Purchases'
                    });
                });
            } catch (error) {
                console.log('No purchase data found for party:', error.message);
            }
            
            // Fetch customer payments
            try {
                const customerPaymentsQuery = query(
                    collection(db, `artifacts/${appId}/users/${userId}/payments`),
                    where('customerId', '==', partyId),
                    orderBy('paymentDate', 'desc'),
                    limit(50)
                );
                const customerPaymentsSnapshot = await getDocs(customerPaymentsQuery);
                customerPaymentsSnapshot.forEach(doc => {
                    const data = doc.data();
                    transactions.push({
                        id: doc.id,
                        type: 'Payment',
                        date: data.paymentDate,
                        amount: -(data.amount || 0), // Negative for payments
                        balance: 0,
                        billNumber: data.billId || 'Direct Payment',
                        status: 'Paid',
                        category: 'Payments'
                    });
                });
            } catch (error) {
                console.log('No customer payment data found for party:', error.message);
            }
            
            // Fetch supplier payments
            try {
                const supplierPaymentsQuery = query(
                    collection(db, `artifacts/${appId}/users/${userId}/purchasePayments`),
                    where('supplierId', '==', partyId),
                    orderBy('paymentDate', 'desc'),
                    limit(50)
                );
                const supplierPaymentsSnapshot = await getDocs(supplierPaymentsQuery);
                supplierPaymentsSnapshot.forEach(doc => {
                    const data = doc.data();
                    transactions.push({
                        id: doc.id,
                        type: 'Payment',
                        date: data.paymentDate,
                        amount: -(data.amount || 0), // Negative for payments
                        balance: 0,
                        billNumber: data.billId || 'Direct Payment',
                        status: 'Paid',
                        category: 'Payments'
                    });
                });
            } catch (error) {
                console.log('No supplier payment data found for party:', error.message);
            }
            
            // Sort transactions by date (newest first)
            transactions.sort((a, b) => new Date(b.date) - new Date(a.date));
            setPartyTransactions(transactions);
            
        } catch (error) {
            console.error('Error fetching party transactions:', error);
            setMessage('Error fetching transaction data. Please try again.');
        } finally {
            setIsLoadingTransactions(false);
        }
    };

    // Function to calculate aging analysis for a party
    const calculatePartyAging = async (partyId) => {
        if (!db || !userId || !appId || !partyId) return;
        
        setIsLoadingAging(true);
        try {
            const agingData = {
                current: 0,      // 0-30 days
                days31to60: 0,   // 31-60 days
                days61to90: 0,   // 61-90 days
                days91to180: 0,  // 91-180 days
                over180: 0,      // Over 180 days
                total: 0
            };
            
            const today = new Date();
            
            // Process sales bills for aging
            try {
                const salesQuery = query(
                    collection(db, `artifacts/${appId}/users/${userId}/sales`),
                    where('customerId', '==', partyId),
                    where('balanceAmount', '>', 0)
                );
                const salesSnapshot = await getDocs(salesQuery);
                salesSnapshot.forEach(doc => {
                    const data = doc.data();
                    const billDate = new Date(data.billDate);
                    const daysDiff = Math.floor((today - billDate) / (1000 * 60 * 60 * 24));
                    const balance = data.balanceAmount || 0;
                    
                    if (daysDiff <= 30) {
                        agingData.current += balance;
                    } else if (daysDiff <= 60) {
                        agingData.days31to60 += balance;
                    } else if (daysDiff <= 90) {
                        agingData.days61to90 += balance;
                    } else if (daysDiff <= 180) {
                        agingData.days91to180 += balance;
                    } else {
                        agingData.over180 += balance;
                    }
                    agingData.total += balance;
                });
            } catch (error) {
                console.log('No sales data found for aging:', error.message);
            }
            
            // Process purchase bills for aging
            try {
                const purchaseQuery = query(
                    collection(db, `artifacts/${appId}/users/${userId}/purchases`),
                    where('supplierId', '==', partyId),
                    where('balanceAmount', '>', 0)
                );
                const purchaseSnapshot = await getDocs(purchaseQuery);
                purchaseSnapshot.forEach(doc => {
                    const data = doc.data();
                    const billDate = new Date(data.billDate);
                    const daysDiff = Math.floor((today - billDate) / (1000 * 60 * 60 * 24));
                    const balance = data.balanceAmount || 0;
                    
                    if (daysDiff <= 30) {
                        agingData.current += balance;
                    } else if (daysDiff <= 60) {
                        agingData.days31to60 += balance;
                    } else if (daysDiff <= 90) {
                        agingData.days61to90 += balance;
                    } else if (daysDiff <= 180) {
                        agingData.days91to180 += balance;
                    } else {
                        agingData.over180 += balance;
                    }
                    agingData.total += balance;
                });
            } catch (error) {
                console.log('No purchase data found for aging:', error.message);
            }
            
            setPartyAgingData(agingData);
            
        } catch (error) {
            console.error('Error calculating aging analysis:', error);
            setMessage('Error calculating aging analysis. Please try again.');
        } finally {
            setIsLoadingAging(false);
        }
    };



    // Function to load audit trail from localStorage
    useEffect(() => {
        const savedAuditTrail = localStorage.getItem(`auditTrail_${userId}_${appId}`);
        if (savedAuditTrail) {
            try {
                setAuditTrail(JSON.parse(savedAuditTrail));
            } catch (error) {
                console.error('Error loading audit trail:', error);
            }
        }
    }, [userId, appId]);

    // Function to save audit trail to localStorage
    useEffect(() => {
        if (auditTrail.length > 0) {
            localStorage.setItem(`auditTrail_${userId}_${appId}`, JSON.stringify(auditTrail));
        }
    }, [auditTrail, userId, appId]);

    // Initialize calculation date and financial year
    useEffect(() => {
        if (!calculationDate) {
            setCalculationDate(new Date().toISOString().split('T')[0]);
        }
        if (!financialYear) {
            setFinancialYear(getCurrentFinancialYear());
        }
    }, [calculationDate, financialYear]);

    const renderPartiesInformation = () => {
        const formProps = {
            editingPartyId,
            t,
            contactSuggestion,
            onAcceptContactSuggestion: handleAcceptContactSuggestion,
            onRejectContactSuggestion: handleRejectContactSuggestion,
            handleSubmit,
            firmName, setFirmName,
            partyType, setPartyType,
            setIsServiceVendor,
            isServiceVendor,
            vendorServiceTags, setVendorServiceTags,
            serviceTagOptions,
            serviceTagMaster,
            personName, setPersonName,
            contact, setContact,
            isSearchingContact,
            isMobileDevice,
            email, setEmail,
            whatsapp, setWhatsapp,
            gstin, setGstin,
            setGstValidation,
            handleCompanyDetailsFetch,
            gstType, setGstType,
            pan, setPan,
            city, setCity,
            stateName, setStateName,
            indianStates,
            pincode, setPincode,
            creditPeriod, setCreditPeriod,
            creditLimit, setCreditLimit,
            openingBalance, setOpeningBalance,
            address, setAddress,
            clearPartyForm,
            db, companyId, appId, userId,
        };

        const listProps = {
            t,
            partiesList,
            filteredParties,
            tableRows: sortedParties,
            sortConfig,
            handleSort,
            searchTerm,
            filterType,
            filterGstType,
            filterBalance,
            onFilterChange: (filters) => {
                setSearchTerm(filters.search || '');
                setFilterGstType(filters.gstType || 'All');
                setFilterBalance(filters.balance || 'All');
            },
            onQuickType: setFilterType,
            onClearFilters: () => {
                setSearchTerm('');
                setFilterType('All');
                setFilterGstType('All');
                setFilterBalance('All');
            },
            selectedForDeletion,
            onToggleSelect: handleDeleteSelection,
            onSelectAllPage: handleSelectAllForDeletion,
            onEdit: handleEditParty,
            onDelete: handleDeleteParty,
            onSelect: handleSelectParty,
            onBulkDeleteClick: () => setShowBulkDeleteConfirm(true),
            isBulkDeleting,
            matchedCount: filteredParties.length,
            totalCount: partiesList.length,
        };

        return (
            <PartiesCatalogTab
                formSectionRef={formSectionRef}
                editingPartyId={editingPartyId}
                buyerCount={partyStats.buyers}
                supplierCount={partyStats.suppliers}
                totalCount={partyStats.total}
                message={message}
                formProps={formProps}
                listProps={listProps}
                isMobileDevice={isMobileDevice}
                onSyncContacts={handleSyncContacts}
                onSelectContact={() => setShowContactSelector(true)}
                syncingContacts={syncingContacts}
            />
        );
    };

    // Render Parties Outstandings Tab
    const renderPartiesOutstandings = () => (
        <div className="space-y-6">
            {/* Party Selection Dropdown for Outstanding Tab */}
            <div className="mb-6">
                <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-4 sm:p-5">
                    <label htmlFor="partySelect" className="block text-sm font-semibold text-slate-800 mb-2">
                        Select party to view outstanding details
                        <span className="text-xs font-normal text-slate-500 ml-2">({partiesList.length} parties available)</span>
                    </label>
                    {partiesList.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                            {partiesList.length === 0 && !isLoadingTransactions ? (
                                "No parties found. Add parties in the Directory tab first."
                            ) : (
                                "Loading parties..."
                            )}
                        </div>
                    ) : (
                        <div className="relative">
                            <input
                                type="text"
                                id="partySelect"
                                placeholder="Type to search parties..."
                                value={partySearchTerm}
                                onChange={(e) => setPartySearchTerm(e.target.value)}
                                className="w-full px-3 py-2.5 pr-20 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                                onFocus={() => {
                                    console.log('Input focused, showing dropdown');
                                    setShowPartyDropdown(true);
                                }}
                            />
                            {/* Clear Button */}
                            {partySearchTerm && (
                                <button
                                    onClick={() => {
                                        console.log('Clear button clicked');
                                        setPartySearchTerm('');
                                        setSelectedParty(null);
                                        setShowPartyDropdown(false);
                                    }}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    title={t('clearSelection')}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                            {showPartyDropdown && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-auto">
                                    {partiesList
                                        .filter((p) => {
                                            if (!partySearchTerm.trim()) return true;
                                            const q = partySearchTerm.toLowerCase();
                                            return (
                                                p.firmName?.toLowerCase().includes(q) ||
                                                p.contact?.includes(partySearchTerm) ||
                                                p.personName?.toLowerCase().includes(q)
                                            );
                                        })
                                        .slice(0, 50)
                                        .map((party) => (
                                        <div
                                            key={party.id}
                                            role="option"
                                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 dropdown-item"
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                handleSelectParty(party);
                                                setPartySearchTerm(party.firmName || '');
                                                setShowPartyDropdown(false);
                                            }}
                                        >
                                            <div className="font-medium text-gray-900">{party.firmName}</div>
                                            <div className="text-sm text-gray-500">
                                                {party.partyType} • {party.contact || 'No Contact'}
                                            </div>
                                        </div>
                                    ))}
                                    {partiesList.filter((p) => {
                                        if (!partySearchTerm.trim()) return true;
                                        const q = partySearchTerm.toLowerCase();
                                        return (
                                            p.firmName?.toLowerCase().includes(q) ||
                                            p.contact?.includes(partySearchTerm) ||
                                            p.personName?.toLowerCase().includes(q)
                                        );
                                    }).length === 0 && (
                                        <div className="px-3 py-2 text-gray-500 text-center">
                                            No parties found
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {/* Selected Party Info */}
                    {selectedParty && (
                        <div className="mt-3 p-3 bg-teal-50 border border-teal-200/80 rounded-xl">
                            <div className="flex items-center justify-between">
                                <div>
                                    <span className="text-sm font-medium text-teal-800">Selected: </span>
                                    <span className="text-sm text-teal-900">{selectedParty.firmName}</span>
                                    <span className="text-xs text-teal-600 ml-2">({selectedParty.partyType})</span>
                                </div>
                                <button
                                    onClick={() => {
                                        console.log('Change Party button clicked');
                                        setSelectedParty(null);
                                        setPartySearchTerm('');
                                    }}
                                    className="text-xs text-teal-700 hover:text-teal-900 underline"
                                >
                                    Change Party
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {selectedParty ? (
                <div>
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg sm:text-xl font-bold text-gray-800">
                            Outstanding Details - {selectedParty.firmName}
                        </h3>
                        <button
                            onClick={() => {
                                console.log('Back to All Parties button clicked');
                                setSelectedParty(null);
                            }}
                            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                        >
                            ← Back to All Parties
                        </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-gradient-to-br from-slate-50 to-white p-5 rounded-xl border border-slate-200/80 shadow-sm">
                            <h4 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-2">Opening balance</h4>
                            <div className={`text-2xl font-bold ${selectedParty.openingBalance > 0 ? 'text-green-600' : selectedParty.openingBalance < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                {selectedParty.openingBalance > 0 ? `+₹${selectedParty.openingBalance.toLocaleString()}` : 
                                 selectedParty.openingBalance < 0 ? `-₹${Math.abs(selectedParty.openingBalance).toLocaleString()}` : 
                                 '₹0.00'}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                                {selectedParty.openingBalance > 0 ? 'Receivable (Customer owes you)' : 
                                 selectedParty.openingBalance < 0 ? 'Payable (You owe supplier)' : 
                                 'No opening balance'}
                            </p>
                        </div>
                        
                        <div className="bg-gradient-to-br from-red-50 to-rose-50 p-5 rounded-xl border border-red-100 shadow-sm">
                            <h4 className="text-sm font-semibold text-red-700 uppercase tracking-wide mb-2">Current outstanding</h4>
                            <div className="text-2xl font-bold text-red-700">
                                ₹{calculateOutstanding(selectedParty).toLocaleString()}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">Based on transactions</p>
                        </div>
                        
                        <div className="bg-gradient-to-br from-indigo-50 to-violet-50/80 p-5 rounded-xl border border-indigo-100/80 shadow-sm">
                            <h4 className="text-sm font-semibold text-indigo-700 uppercase tracking-wide mb-2">Party type</h4>
                            <div className="text-lg font-medium text-gray-700">
                                {selectedParty.partyType}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">Business relationship</p>
                        </div>
                    </div>
                    
                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                        <h4 className="text-lg font-semibold text-gray-800 mb-4">Transaction Summary</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h5 className="font-medium text-gray-700">Recent Transactions</h5>
                                <TransactionSummary party={selectedParty} />
                            </div>
                            
                            <div className="space-y-4">
                                <h5 className="font-medium text-gray-700">Aging Analysis</h5>
                                <AgingAnalysis party={selectedParty} />
                            </div>
                        </div>
                    </div>

                    {/* Customer Ledger Summary Frame */}
                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-semibold text-gray-800">Customer Ledger Summary</h4>
                            <button
                                onClick={() => window.open(`/reports/customer-ledger?party=${selectedParty.id}`, '_blank')}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                            >
                                View Full Report
                            </button>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            {/* Party Type Badge */}
                            <div className="text-center mb-4">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                    selectedParty.partyType === 'Buyer' 
                                        ? 'bg-blue-100 text-blue-800' 
                                        : 'bg-green-100 text-green-800'
                                }`}>
                                    {selectedParty.partyType === 'Buyer' ? '👤 Customer' : '🏭 Supplier'}
                                </span>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <div className="text-center">
                                    <div className="text-sm text-gray-600 mb-1">Opening Balance</div>
                                    <div className={`text-lg font-semibold ${selectedParty.openingBalance > 0 ? 'text-green-600' : selectedParty.openingBalance < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                        {selectedParty.openingBalance > 0 ? `+₹${selectedParty.openingBalance.toLocaleString()}` : 
                                         selectedParty.openingBalance < 0 ? `-₹${Math.abs(selectedParty.openingBalance).toLocaleString()}` : 
                                         '₹0.00'}
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="text-sm text-gray-600 mb-1">Current Balance</div>
                                    <div className="text-lg font-semibold text-blue-600">
                                        ₹{calculateOutstanding(selectedParty).toLocaleString()}
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="text-sm text-gray-600 mb-1">Net Change</div>
                                    <div className={`text-lg font-semibold ${(calculateOutstanding(selectedParty) - selectedParty.openingBalance) > 0 ? 'text-green-600' : (calculateOutstanding(selectedParty) - selectedParty.openingBalance) < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                        {(calculateOutstanding(selectedParty) - selectedParty.openingBalance) > 0 ? `+₹${(calculateOutstanding(selectedParty) - selectedParty.openingBalance).toLocaleString()}` : 
                                         (calculateOutstanding(selectedParty) - selectedParty.openingBalance) < 0 ? `-₹${Math.abs(calculateOutstanding(selectedParty) - selectedParty.openingBalance).toLocaleString()}` : 
                                         '₹0.00'}
                                    </div>
                                </div>
                            </div>
                            <div className="text-center">
                                <p className="text-sm text-gray-600">
                                    {t('detailedTransactionHistory')}
                                </p>
                            </div>
                            
                            {/* Credit Information */}
                            {(selectedParty.creditPeriod > 0 || selectedParty.creditLimit > 0) && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <h5 className="text-sm font-medium text-gray-700 mb-3 text-center">Credit Terms</h5>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                        {selectedParty.creditPeriod > 0 && (
                                            <div className="text-center">
                                                <div className="text-gray-600 mb-1">Credit Period</div>
                                                <div className="font-semibold text-gray-800">
                                                    {selectedParty.creditPeriod} days
                                                </div>
                                            </div>
                                        )}
                                        {selectedParty.creditLimit > 0 && (
                                            <div className="text-center">
                                                <div className="text-gray-600 mb-1">Credit Limit</div>
                                                <div className="font-semibold text-gray-800">
                                                    ₹{selectedParty.creditLimit.toLocaleString()}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            {/* Quick Transaction Preview */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <h5 className="text-sm font-medium text-gray-700 mb-3 text-center">Recent Activity Summary</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div className="text-center">
                                        <div className="text-gray-600 mb-1">Total Transactions</div>
                                        <div className="font-semibold text-gray-800">
                                            {partyTransactions.length}
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-gray-600 mb-1">Last Transaction</div>
                                        <div className="font-semibold text-gray-800">
                                            {partyTransactions.length > 0 ? new Date(partyTransactions[0]?.date?.toDate?.() || partyTransactions[0]?.date).toLocaleDateString() : 'N/A'}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Outstanding Status */}
                                {calculateOutstanding(selectedParty) > 0 && (
                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                        <div className="text-center">
                                            <div className="text-orange-600 text-sm font-medium mb-1">⚠️ Outstanding Amount</div>
                                            <div className="text-lg font-bold text-orange-700">
                                                ₹{calculateOutstanding(selectedParty).toLocaleString()}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                {selectedParty.partyType === 'Buyer' ? 'Customer owes you' : 'You owe supplier'}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Party</h3>
                    <p className="text-gray-500">Search above or click a party in the Directory tab to view outstanding details.</p>
                </div>
            )}
        </div>
    );

    // Render Opening Balance Management Tab
    const renderOpeningBalanceManagement = () => (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-teal-50/90 via-white to-indigo-50/80 border border-teal-100/80 rounded-xl p-5 sm:p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-white rounded-xl border border-teal-100 shadow-sm">
                        <Building2 className="w-6 h-6 text-teal-600" aria-hidden />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-slate-900">Opening balance management</h3>
                        <p className="text-sm text-slate-500">Manage financial year transitions and opening balance calculations</p>
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white p-4 rounded-xl border border-teal-100/60 shadow-sm">
                        <h4 className="text-xs font-semibold text-teal-700/80 uppercase tracking-wide mb-2">Current financial year</h4>
                        <div className="text-2xl font-bold text-teal-800">
                            {getCurrentFinancialYear()}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Active financial year</p>
                    </div>
                    
                    <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm">
                        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Parties count</h4>
                        <div className="text-2xl font-bold text-slate-900">
                            {partiesList.length}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Total parties in system</p>
                    </div>
                </div>
            </div>

            {/* Year-End Processing Section */}
            <div className="bg-white border border-slate-200/80 rounded-xl p-5 sm:p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-bold text-slate-900">Financial year-end processing</h4>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setShowAuditTrail(true)}
                            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors text-sm"
                        >
                            View Audit Trail
                        </button>
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                         <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                         <h5 className="font-medium text-blue-700 mb-2">Step 1: Calculate</h5>
                         <p className="text-sm text-blue-600">Calculate opening balances based on current year transactions</p>
                         <button
                             onClick={calculateAllOpeningBalances}
                             disabled={isCalculating || partiesList.length === 0}
                             className="mt-3 w-full px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                         >
                             {isCalculating ? 'Calculating...' : 'Calculate Opening Balances'}
                         </button>
                     </div>
                    
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <h5 className="font-medium text-green-700 mb-2">Step 2: Review</h5>
                        <p className="text-sm text-green-600">Review calculated balances before applying</p>
                        <button
                            onClick={() => setShowCalculationPreview(true)}
                            disabled={calculationData.length === 0}
                            className="mt-3 w-full px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                        >
                            Review Calculations ({calculationData.length})
                        </button>
                    </div>
                    
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <h5 className="font-medium text-purple-700 mb-2">Step 3: Apply</h5>
                        <p className="text-sm text-purple-600">Apply calculated balances to parties</p>
                        <button
                            onClick={applyCalculatedOpeningBalances}
                            disabled={calculationData.length === 0}
                            className="mt-3 w-full px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                        >
                            Apply Opening Balances
                        </button>
                    </div>
                </div>

                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="flex items-start">
                        <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <div>
                            <h5 className="font-medium text-yellow-800">Important Notes</h5>
                                                         <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                                 <li>• This process calculates opening balances based on current year transactions</li>
                                 <li>• Universal Formula: Opening Balance + Sales - Payment Received - Purchase + Payment Paid</li>
                                 <li>• Payment Received (PRI): Customer receipts against sales invoices</li>
                                 <li>• Payment Paid (PRP): Supplier payments against purchase bills</li>
                                 <li>• +ve Opening Balance: Customer owes you money (Receivable)</li>
                                 <li>• -ve Opening Balance: You owe supplier money (Payable)</li>
                                 <li>• Review all calculations before applying to ensure accuracy</li>
                                 <li>• This action cannot be undone automatically</li>
                             </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Manual Balance Management Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Manual Balance Management</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Balance Type</label>
                            <div className="flex items-center space-x-4">
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        checked={useManualBalance}
                                        onChange={() => setUseManualBalance(true)}
                                        className="mr-2 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">Manual Entry</span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        checked={!useManualBalance}
                                        onChange={() => setUseManualBalance(false)}
                                        className="mr-2 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">Calculated</span>
                                </label>
                            </div>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Manual Balance (₹)</label>
                            <input
                                type="number"
                                value={manualBalance}
                                onChange={(e) => setManualBalance(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                                step="0.01"
                            />
                        </div>
                    </div>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Balance Date</label>
                            <input
                                type="date"
                                value={manualBalanceDate}
                                onChange={(e) => setManualBalanceDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                            <textarea
                                value={manualBalanceRemarks}
                                onChange={(e) => setManualBalanceRemarks(e.target.value)}
                                rows="2"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter remarks for this balance..."
                            />
                        </div>
                    </div>
                </div>
                
                {selectedParty ? (
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <h5 className="font-medium text-blue-700 mb-1">Update Balance for {selectedParty.firmName}</h5>
                                <p className="text-sm text-blue-600">
                                    Current balance: ₹{(parseFloat(selectedParty.openingBalance) || 0).toLocaleString('en-IN')}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={updateManualBalance}
                                disabled={
                                    manualBalance === '' ||
                                    parseFloat(manualBalance) === (parseFloat(selectedParty.openingBalance) || 0)
                                }
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                            >
                                Update Balance
                            </button>
                        </div>
                    </div>
                ) : (
                    <p className="mt-4 text-sm text-gray-500">
                        Select a party from the Directory or Outstandings tab to update manual opening balance here.
                    </p>
                )}
            </div>

            {/* Calculation Preview Modal */}
            {showCalculationPreview && (
                <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                    <div className="bg-white rounded-t-2xl sm:rounded-lg max-w-6xl w-full max-h-[92dvh] overflow-hidden flex flex-col">
                        <div className="p-4 sm:p-6 border-b border-gray-200 shrink-0">
                            <div className="flex items-center justify-between gap-3">
                                <h3 className="text-lg font-semibold text-gray-800">Opening Balance Calculation Preview</h3>
                                <button
                                    onClick={() => setShowCalculationPreview(false)}
                                    className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 touch-manipulation"
                                    aria-label="Close"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">Review calculated opening balances for FY {financialYear}</p>
                        </div>
                        
                        <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
                            {/* Column Information */}
                            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-2">Column Information:</p>
                                    <ul className="space-y-1 text-xs">
                                        <li><strong>Sales:</strong> Total sales bills in current financial year</li>
                                        <li><strong>Purchases:</strong> Total purchase bills in current financial year</li>
                                        <li><strong>Payment Received:</strong> Customer receipts (PRI - against sales bills)</li>
                                        <li><strong>Payment Paid:</strong> Supplier payments (PRP - against purchase bills)</li>
                                        <li><strong>Calculated Balance:</strong> Opening Balance + Sales - Payment Received - Purchase + Payment Paid</li>
                                    </ul>
                                </div>
                            </div>
                            
                            <div className="md:hidden space-y-2 mb-4">
                                {calculationData.map((calc) => (
                                    <div key={calc.partyId} className="border border-gray-200 rounded-lg p-3 bg-white">
                                        <div className="font-medium text-gray-900 text-sm mb-1">{calc.partyName}</div>
                                        <div className="text-xs text-gray-500 mb-2">{calc.partyType}</div>
                                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600">
                                            <span>Prev: ₹{calc.previousBalance.toLocaleString()}</span>
                                            <span>Sales: ₹{calc.totalSales.toLocaleString()}</span>
                                            <span>Purch: ₹{calc.totalPurchases.toLocaleString()}</span>
                                            <span>Received: ₹{(calc.totalPayments || 0).toLocaleString()}</span>
                                            <span>Paid: ₹{(calc.totalPurchasePayments || 0).toLocaleString()}</span>
                                        </div>
                                        <div className={`mt-2 pt-2 border-t border-gray-100 text-sm font-semibold ${calc.calculatedBalance > 0 ? 'text-green-600' : calc.calculatedBalance < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                            Balance: ₹{calc.calculatedBalance.toLocaleString()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="hidden md:block overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Party</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Previous Balance</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sales</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purchases</th>
                                                                                         <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Received (PRI)</th>
                                             <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Paid (PRP)</th>
                                             <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Calculated Balance</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {calculationData.map((calc, index) => (
                                            <tr key={calc.partyId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{calc.partyName}</td>
                                                <td className="px-4 py-3 text-sm text-gray-500">{calc.partyType}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900">
                                                    ₹{calc.previousBalance.toLocaleString()}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900">
                                                    ₹{calc.totalSales.toLocaleString()}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900">
                                                    ₹{calc.totalPurchases.toLocaleString()}
                                                </td>
                                                                                                                                          <td className="px-4 py-3 text-sm text-gray-900">
                                                 ₹{(calc.totalPayments || 0).toLocaleString()}
                                             </td>
                                             <td className="px-4 py-3 text-sm text-gray-900">
                                                 ₹{(calc.totalPurchasePayments || 0).toLocaleString()}
                                             </td>
                                             <td className="px-4 py-3 text-sm font-medium">
                                                    <span className={`${calc.calculatedBalance > 0 ? 'text-green-600' : calc.calculatedBalance < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                                        ₹{calc.calculatedBalance.toLocaleString()}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        
                        <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50 shrink-0">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                                <div className="text-sm text-gray-600">
                                    Total parties: {calculationData.length}
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                    <button
                                        onClick={() => setShowCalculationPreview(false)}
                                        className="min-h-[44px] px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors touch-manipulation"
                                    >
                                        Close
                                    </button>
                                    <button
                                        onClick={applyCalculatedOpeningBalances}
                                        className="min-h-[44px] px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors touch-manipulation"
                                    >
                                        Apply All Changes
                                    </button>
                                    <button
                                        onClick={exportCalculationData}
                                        className="min-h-[44px] px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors touch-manipulation"
                                    >
                                        Export CSV
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Audit Trail Modal */}
            {showAuditTrail && (
                <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                    <div className="bg-white rounded-t-2xl sm:rounded-lg max-w-4xl w-full max-h-[92dvh] overflow-hidden flex flex-col">
                        <div className="p-4 sm:p-6 border-b border-gray-200 shrink-0">
                            <div className="flex items-center justify-between gap-3">
                                <h3 className="text-lg font-semibold text-gray-800">Audit Trail</h3>
                                <button
                                    onClick={() => setShowAuditTrail(false)}
                                    className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 touch-manipulation"
                                    aria-label="Close"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">Track all opening balance changes and calculations</p>
                        </div>
                        
                        <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
                            {auditTrail.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <p>No audit trail entries yet.</p>
                                    <p className="text-sm mt-1">Perform opening balance calculations to see audit entries.</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {auditTrail.map((entry) => (
                                        <div key={entry.id} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                                                            {entry.action}
                                                        </span>
                                                        <span className="text-sm text-gray-500">
                                                            {new Date(entry.timestamp).toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <h4 className="font-medium text-gray-900 mb-1">
                                                        {entry.details}
                                                    </h4>
                                                    <div className="text-sm text-gray-600 space-y-1">
                                                        {entry.financialYear && (
                                                            <p><strong>Financial Year:</strong> {entry.financialYear}</p>
                                                        )}
                                                        {entry.partiesCalculated && (
                                                            <p><strong>Parties:</strong> {entry.partiesCalculated}</p>
                                                        )}
                                                        {entry.calculationDate && (
                                                            <p><strong>Calculation Date:</strong> {new Date(entry.calculationDate).toLocaleDateString()}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50 shrink-0">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                                <div className="text-sm text-gray-600">
                                    Total entries: {auditTrail.length}
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                    {auditTrail.length > 0 && (
                                        <>
                                            <button
                                                onClick={exportAuditTrail}
                                                className="min-h-[44px] px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors touch-manipulation"
                                            >
                                                Export CSV
                                            </button>
                                            <button
                                                onClick={clearAuditTrail}
                                                className="min-h-[44px] px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors touch-manipulation"
                                            >
                                                Clear Trail
                                            </button>
                                        </>
                                    )}
                                    <button
                                        onClick={() => setShowAuditTrail(false)}
                                        className="min-h-[44px] px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors touch-manipulation"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    // Transaction Summary Component
    const TransactionSummary = ({ party }) => {
        if (!party) return null;
        
        if (isLoadingTransactions) {
            return (
                <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-sm text-gray-600">Loading transactions...</span>
                    </div>
                </div>
            );
        }

        if (partyTransactions.length === 0) {
            return (
                <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">No transactions found for this party.</p>
                </div>
            );
        }

        const recentTransactions = partyTransactions.slice(0, 10);
        const totalAmount = partyTransactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);
        const outstandingAmount = partyTransactions
            .filter(t => t.balance > 0)
            .reduce((sum, t) => sum + t.balance, 0);

        return (
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-blue-50 p-3 rounded-lg">
                        <div className="text-xs text-blue-600 font-medium">Total Transactions</div>
                        <div className="text-lg font-bold text-blue-800">₹{totalAmount.toLocaleString()}</div>
                    </div>
                    <div className="bg-orange-50 p-3 rounded-lg">
                        <div className="text-xs text-orange-600 font-medium">Outstanding</div>
                        <div className="text-lg font-bold text-orange-800">₹{outstandingAmount.toLocaleString()}</div>
                    </div>
                </div>
                
                <div className="space-y-2">
                    <h6 className="font-medium text-gray-700 text-sm">Recent Transactions</h6>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                        {recentTransactions.map((transaction) => (
                            <div key={transaction.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-xs">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            transaction.type === 'Sale' ? 'bg-green-100 text-green-800' :
                                            transaction.type === 'Purchase' ? 'bg-blue-100 text-blue-800' :
                                            'bg-purple-100 text-purple-800'
                                        }`}>
                                            {transaction.type}
                                        </span>
                                        <span className="text-gray-600">{transaction.billNumber}</span>
                                    </div>
                                    <div className="text-gray-500 mt-1">
                                        {new Date(transaction.date).toLocaleDateString()}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className={`font-medium ${
                                        transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                        {transaction.amount >= 0 ? '+' : ''}₹{Math.abs(transaction.amount).toLocaleString()}
                                    </div>
                                    {transaction.balance > 0 && (
                                        <div className="text-xs text-orange-600">
                                            Balance: ₹{transaction.balance.toLocaleString()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // Aging Analysis Component
    const AgingAnalysis = ({ party }) => {
        if (!party) return null;
        
        if (isLoadingAging) {
            return (
                <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                        <span className="ml-2 text-sm text-gray-600">Calculating aging...</span>
                    </div>
                </div>
            );
        }

        if (partyAgingData.total === 0) {
            return (
                <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">No outstanding amounts for aging analysis.</p>
                </div>
            );
        }

        const agingBars = [
            { label: 'Current (0-30 days)', value: partyAgingData.current, color: 'bg-green-500', percentage: (partyAgingData.current / partyAgingData.total * 100).toFixed(1) },
            { label: '31-60 days', value: partyAgingData.days31to60, color: 'bg-yellow-500', percentage: (partyAgingData.days31to60 / partyAgingData.total * 100).toFixed(1) },
            { label: '61-90 days', value: partyAgingData.days61to90, color: 'bg-orange-500', percentage: (partyAgingData.days61to90 / partyAgingData.total * 100).toFixed(1) },
            { label: '91-180 days', value: partyAgingData.days91to180, color: 'bg-red-500', percentage: (partyAgingData.days91to180 / partyAgingData.total * 100).toFixed(1) },
            { label: 'Over 180 days', value: partyAgingData.over180, color: 'bg-purple-500', percentage: (partyAgingData.over180 / partyAgingData.total * 100).toFixed(1) }
        ];

        return (
            <div className="space-y-4">
                <div className="bg-orange-50 p-3 rounded-lg">
                    <div className="text-xs text-orange-600 font-medium">Total Outstanding</div>
                    <div className="text-lg font-bold text-orange-800">₹{partyAgingData.total.toLocaleString()}</div>
                </div>
                
                <div className="space-y-3">
                    {agingBars.map((bar, index) => (
                        <div key={index} className="space-y-1">
                            <div className="flex justify-between text-xs">
                                <span className="text-gray-600">{bar.label}</span>
                                <span className="font-medium text-gray-800">
                                    ₹{bar.value.toLocaleString()} ({bar.percentage}%)
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                    className={`${bar.color} h-2 rounded-full transition-all duration-300`}
                                    style={{ width: `${bar.percentage}%` }}
                                ></div>
                            </div>
                        </div>
                    ))}
                </div>
                
                <div className="text-xs text-gray-500 mt-4">
                    <p>• <strong>Current:</strong> Within 30 days - Good</p>
                    <p>• <strong>31-60 days:</strong> Attention needed</p>
                    <p>• <strong>61+ days:</strong> Immediate action required</p>
                </div>
            </div>
        );
    };

    // Show loading screen while initial data is being fetched
    // Show if we're in initial loading state (set immediately on mount, before any data operations)
    const shouldShowLoading = isInitialLoading && db && userId && isAuthReady;
    
    if (shouldShowLoading) {
        console.log('🔄 [LOADING SCREEN] Parties: RENDERING LOADING SCREEN NOW', {
            isInitialLoading,
            partiesCount: partiesList.length,
            hasDb: !!db,
            hasUserId: !!userId,
            isAuthReady,
            hasCompanyId: !!companyId,
            dataLoaded: dataLoadedRef.current
        });
        return (
            <LoadingScreen 
                message="Loading Parties..."
                subMessage="Retrieving your customer and supplier relationships"
                fullScreen={true}
            />
        );
    }

    return (
        <div className={`flex flex-col w-full min-h-[calc(100dvh-4.75rem)] lg:h-[calc(100dvh-4.75rem)] overflow-visible lg:overflow-hidden ${PARTIES_THEME.pageBg} mobile-content`}>
            <div className="flex flex-col flex-1 min-h-0 w-full max-w-none overflow-visible lg:overflow-hidden p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 shrink-0">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white flex-shrink-0 ${PARTIES_THEME.iconTile}`}>
                            <Users className="w-5 h-5" aria-hidden />
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                                {PARTIES_THEME.title}
                            </h1>
                            <p className="text-sm text-slate-500 mt-0.5">{PARTIES_THEME.subtitle}</p>
                        </div>
                    </div>
                </div>

                {activeTab === 'information' && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 shrink-0">
                        <div className={PARTIES_THEME.statCardTotal}>
                            <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Total parties</div>
                            <div className="text-xl font-bold text-teal-900 mt-1">{partyStats.total}</div>
                        </div>
                        <div className={PARTIES_THEME.statCardCustomers}>
                            <div className="text-[11px] font-medium text-emerald-700 uppercase tracking-wide">Customers</div>
                            <div className="text-xl font-bold text-emerald-800 mt-1">{partyStats.buyers}</div>
                        </div>
                        <div className={PARTIES_THEME.statCardSuppliers}>
                            <div className="text-[11px] font-medium text-indigo-700 uppercase tracking-wide">Suppliers</div>
                            <div className="text-xl font-bold text-indigo-800 mt-1">{partyStats.suppliers}</div>
                        </div>
                        <div className={`${PARTIES_THEME.statCardOther} col-span-2 lg:col-span-1`}>
                            <div className="text-[11px] font-medium text-amber-800/90 uppercase tracking-wide">Both / other</div>
                            <div className="text-xl font-bold text-amber-900 mt-1">
                                {Math.max(0, partyStats.total - partyStats.buyers - partyStats.suppliers)}
                            </div>
                        </div>
                    </div>
                )}

                <PartiesTabNav
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    className="mb-4 shrink-0 w-full"
                />
                <PartiesTabNavMobile
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    className="mb-4 shrink-0 w-full"
                />

                {activeTab === 'information' && (
                    <div className="flex flex-col flex-1 min-h-0 w-full">
                        {renderPartiesInformation()}
                    </div>
                )}

                {activeTab === 'outstandings' && (
                    <div className={`flex flex-col flex-1 min-h-0 w-full ${PARTIES_THEME.contentCard} overflow-auto`}>
                        <div className={`${PARTIES_THEME.panelHeader} shrink-0`}>
                            <h2 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-emerald-600" aria-hidden />
                                Outstandings
                            </h2>
                            <p className="text-xs text-slate-500 mt-1">View dues and aging by party</p>
                        </div>
                        <div className="p-4 sm:p-5 flex-1 min-h-0 overflow-auto">
                            {renderPartiesOutstandings()}
                        </div>
                    </div>
                )}

                {activeTab === 'opening-balance' && (
                    <div className={`flex flex-col flex-1 min-h-0 w-full ${PARTIES_THEME.contentCard} overflow-auto`}>
                        <div className={`${PARTIES_THEME.panelHeader} shrink-0`}>
                            <h2 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-teal-600" aria-hidden />
                                Opening balance
                            </h2>
                            <p className="text-xs text-slate-500 mt-1">Financial year transitions and balance setup</p>
                        </div>
                        <div className="p-4 sm:p-5 flex-1 min-h-0 overflow-auto">
                            {renderOpeningBalanceManagement()}
                        </div>
                    </div>
                )}
            </div>

            {/* Contact Selector Modal */}
            <ContactSelector
                isOpen={showContactSelector}
                onClose={() => setShowContactSelector(false)}
                onSelect={handleSelectContact}
                appId={appId}
                userId={userId}
                placeholder="Search contacts by name or number..."
                showSyncButton={true}
            />

            <ConfirmationModal
                section={MODAL_SECTIONS.PARTIES}
                isOpen={showBulkDeleteConfirm}
                onClose={() => setShowBulkDeleteConfirm(false)}
                title="Confirm Bulk Delete"
                message={`Are you sure you want to delete ${selectedForDeletion.size} selected party(ies)? This action cannot be undone.`}
                onConfirm={handleBulkDelete}
                confirmText={isBulkDeleting ? 'Deleting...' : 'Delete Selected'}
                cancelText="Cancel"
                variant="danger"
                confirmDisabled={isBulkDeleting}
            />

        </div>
    );
};

export default Parties; 